// ============================================================
// methods — default step-by-step instructions for a recipe
//
// Single source of truth. These used to live in Method.js with a second,
// slightly different copy hardcoded inside PdfExport.js; the two drifted, so
// exported PDFs showed wording that appeared nowhere in the editor.
// ============================================================

export const DEFAULT_SECTIONS = [
  {
    key: 'casing',
    title: 'Casing Preparation',
    color: '#1A237E',
    steps: [
      'Rinse off the salt and flush the casings with cool water.',
      'Place cleaned casings in fresh water with baking soda (1 tsp per quart) and refrigerate overnight.',
      'If casings are not translucent or are tough, soak overnight in a quart of water with ¾ tsp lemon juice or vinegar.',
    ],
  },
  {
    key: 'meat',
    title: 'Meat Preparation & Stuffing',
    color: '#37474F',
    steps: [
      'Clean meat from any silver skin and arteries. Chill meat and fat to below 34°F.',
      'Grind chilled meat and fat on a coarse plate (10–18mm). Re-chill. Keep temp under 34°F.',
      'Add all ingredients (spices, liquid, binder, cure) to the meat and mix until it becomes a sticky batter.',
      'Stuff the mixture into casings and link. Prick out any air pockets.',
      'Refrigerate overnight to cure.',
    ],
  },
  {
    key: 'cooking',
    title: 'Cooking',
    color: '#B71C1C',
    steps: [
      '** Pellet grill/smoker:\n- 1 hour cold smoke at 90-100F with smoke tube to dry the sausage exterior.\n- Increase to 170F and cook until internal temp reaches 145F for 5 min (about 4-5 hours).',
      '** Digital convection smoker:\n- 1 hour at 100F, fan 50% to dry exterior.\n- 1 hour at 125F, fan 50% to start adding smoke.\n- 1.5 hours at 150F.\n- Increase to 170F and cook until internal temp reaches 145F for 5 min.',
      '** Non-convection digital smoker:\n- 1.5 hours at 100F with door open to dry exterior.\n- 1 hour at 125F to add smoke.\n- 1 hour at 155F.\n- 1 hour at 175F. Increase to 200F and cook until internal temp reaches 145F for 5 min.',
      '** Offset BBQ pit or grill:\n- Two-zone setup. Smoke on indirect side at 150F for 3 hours.\n- Increase to 250F and cook until internal temp reaches 150F for 5 min.',
      'After cooking, place sausages in a cold-water bath until internal temperature reaches 90-100F.',
      'Hang or place on a rack at room temp for 3-4 hours to bloom.',
      'Refrigerate and enjoy.',
    ],
  },
  {
    key: 'storage',
    title: 'Storage',
    color: '#1B5E20',
    steps: [
      'For long-term storage, place smoked sausages in vacuum-sealed bags and freeze.',
      'Sausages will last up to one year in the freezer.',
    ],
  },
];

export function methodUid() {
  return Math.random().toString(36).slice(2, 9);
}

/** Defaults in the shape the editor works with: ids on sections and steps. */
export function buildInitialSections() {
  return DEFAULT_SECTIONS.map(sec => ({
    ...sec,
    id: methodUid(),
    steps: sec.steps.map(text => ({ id: methodUid(), text })),
  }));
}

/** Accept either editor shape or plain {title, steps:[string]} (imports, saved
 *  recipes from before methods were stored) and return the editor shape. */
export function normalizeSections(input) {
  if (!Array.isArray(input) || input.length === 0) return buildInitialSections();
  return input.map(sec => ({
    id:    sec.id || methodUid(),
    key:   sec.key || 'section_' + methodUid(),
    title: sec.title || 'Section',
    color: sec.color || '#546E7A',
    steps: (sec.steps || []).map(step => ({
      id:   (step && step.id) || methodUid(),
      text: typeof step === 'string' ? step : ((step && step.text) || ''),
    })),
  }));
}
